import React from "react";
import styled from "styled-components";
import { ErrorList, ErrorStrings, EnumSurveyType, surveyQuestionTypeOptions } from "../../../components/Enums";
import Skeleton from "src/scenes/Statistics/components/Skeleton";
import { AddAnotherQuestionContainer, QuestionContainer } from "../../../components/Utils";
import { SurveyStatusType } from "src/constants/survey";
import { CustomSelect, ErrorText, PortalFormFieldGroup, PortalFormLabel } from "src/components";
import { SurveySelectOptionContent } from "../DropdownCustomStyles";

const SurveyQuestionsList = styled.div`
    display: grid;
    gap: 0.75rem;
`;

const SurveyQuestionBlock = styled.div`
    display: grid;
    gap: 0.5rem;
`;

const CompactResponseFieldGroup = styled(PortalFormFieldGroup)`
    gap: 0.5rem;

    > label,
    > p {
        margin: 0 0 0.35rem;
    }
`;

export const SurveyQuestionsFields = (props) => {
    const { inputQuestion, inputErrors, survey, addQuestion, removeQuestion, selectQuestionSurveyType, isEditSurveyForm, loading } = props;
    const areQuestionsDisabled = survey.status === SurveyStatusType.ACTIVE && isEditSurveyForm;
    const getSurveyErrorStrings = (...errs) => inputErrors.filter(err => errs.includes(err)).map(err => ErrorStrings[err]);
    const surveyQuestionError = getSurveyErrorStrings(
        ErrorList.QUESTION_MIN_REQUIRED,
        ErrorList.SURVEY_QUESTION_IMMUTABLE,
        ErrorList.EMPTY_QUESTION_SURVEY_TYPE
    );

    return (
        <>
            { loading && 
                <div style={{ paddingTop: "1rem" }}>
                    <Skeleton animation="wave" width="auto" height="50px" />
                </div>
            }
            { !loading && 
                <>
                    <SurveyQuestionItems 
                        surveyQuestions={survey.surveyQuestions} 
                        inputQuestion={inputQuestion} 
                        selectQuestionSurveyType={selectQuestionSurveyType}
                        inputErrors={inputErrors} 
                        removeQuestion={removeQuestion} 
                        areQuestionsDisabled={areQuestionsDisabled}
                        surveyType={survey.surveyType}
                    />

                    {!areQuestionsDisabled && <AddAnotherQuestionContainer addQuestion={addQuestion} />}
                    {surveyQuestionError.length > 0 && (
                        <ErrorText errorMargin="0.75rem 0 0">
                            {surveyQuestionError}
                        </ErrorText>
                    )}
                </>
            }
        </>
    );
};

const SurveyQuestionItems = ({ surveyQuestions, inputQuestion, selectQuestionSurveyType, inputErrors, removeQuestion, areQuestionsDisabled, surveyType }) => {
    const surveyQuestionErrors = (index) => {
        const filteredErrors = inputErrors.filter(err => typeof err === "object" && err.index === index);
        return filteredErrors.map(err => {
            let errorString = ErrorStrings[err.type];
            return errorString.replace("{number}", err.index + 1);
        });
    };
    
    return (
        <SurveyQuestionsList>
            { 
                surveyQuestions.map((surveyQuestion, index) => {
                    const itemQuestionError = surveyQuestionErrors(index);
                    const questionTypeError = inputErrors
                        .filter(err => typeof err === "object" && err.type === ErrorList.EMPTY_QUESTION_SURVEY_TYPE && err.index === index)
                        .map(err => ErrorStrings[err.type]);

                    return (
                        <SurveyQuestionBlock key={index}>
                            <QuestionContainer
                                questionNumber={index + 1}
                                questionValue={surveyQuestion.question}
                                inputQuestion={inputQuestion}
                                surveyQuestionError={itemQuestionError}
                                showRemoveQuestion={index !== 0 && !areQuestionsDisabled}
                                removeQuestion={removeQuestion}
                                isDisabled={areQuestionsDisabled}
                            />
                            {surveyType === EnumSurveyType.MULTI_TYPE && (
                                <CompactResponseFieldGroup>
                                    <PortalFormLabel size="0.875rem" weight={700}>
                                        Question {index + 1} Response Type
                                    </PortalFormLabel>
                                    <CustomSelect
                                        isDisabled={areQuestionsDisabled}
                                        value={surveyQuestionTypeOptions.find(obj => obj.value === surveyQuestion.surveyType)}
                                        placeholder="Please select..."
                                        options={surveyQuestionTypeOptions}
                                        onChange={(choice) => selectQuestionSurveyType(index + 1, choice)}
                                        height="46px"
                                        radius="8px"
                                        fontSize="0.9375rem"
                                        fontWeight={areQuestionsDisabled ? 700 : 400}
                                        placeholderVariant={areQuestionsDisabled ? "muted" : "default"}
                                        placeholderFontWeight={areQuestionsDisabled ? 500 : 400}
                                        renderOption={SurveySelectOptionContent}
                                        renderValue={SurveySelectOptionContent}
                                    />
                                    {questionTypeError.length > 0 && (
                                        <ErrorText errorMargin="0.75rem 0 0">
                                            {questionTypeError}
                                        </ErrorText>
                                    )}
                                </CompactResponseFieldGroup>
                            )}
                        </SurveyQuestionBlock>
                    );
                })
            }
        </SurveyQuestionsList>
    );
};
